import "./style.css";
import * as dat from "lil-gui";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import firefliesVertexShader from "./shaders/fireflies/vertex.glsl";
import firefliesFragmentShader from "./shaders/fireflies/fragment.glsl";
import portalVertexShader from "./shaders/portal/vertex.glsl";
import portalFragmentShader from "./shaders/portal/fragment.glsl";

// const SPECTOR = require("spectorjs");
// const spector = new SPECTOR.Spector();
// spector.displayUI();

/**
 * Base
 */
// Debug
const debugObject = {};

const gui = new dat.GUI({
	width: 400,
});

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Loaders
 */
// Texture loader
const textureLoader = new THREE.TextureLoader();

// Draco loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("draco/");

// GLTF loader
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

/**
 * Object
 */
// const cube = new THREE.Mesh(
// 	new THREE.BoxGeometry(1, 1, 1),
// 	new THREE.MeshBasicMaterial()
// );

// scene.add(cube);

/**
 * Texture
 */
const bakedTexture = textureLoader.load("baked.jpg");
bakedTexture.flipY = false;
bakedTexture.encoding = THREE.sRGBEncoding;

/**
 * Material
 */
// baked material
// const bakedMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
const bakedMaterial = new THREE.MeshBasicMaterial({ map: bakedTexture });

// portal light mat
debugObject.portalColorStart = "#000000";
debugObject.portalColorEnd = "#ffffff";

// const portalLightMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
const portalLightMaterial = new THREE.ShaderMaterial({
	uniforms: {
		uTime: { value: 0 },
		uColorStart: { value: new THREE.Color(debugObject.portalColorStart) },
		uColorEnd: { value: new THREE.Color(debugObject.portalColorEnd) },
	},
	vertexShader: portalVertexShader,
	fragmentShader: portalFragmentShader,
});

gui.addColor(debugObject, "portalColorStart").onChange(() => {
	portalLightMaterial.uniforms.uColorStart.value.set(
		debugObject.portalColorStart
	);
});

gui.addColor(debugObject, "portalColorEnd").onChange(() => {
	portalLightMaterial.uniforms.uColorEnd.value.set(
		debugObject.portalColorEnd
	);
});

// pole light mat
const poleLightMaterial = new THREE.MeshBasicMaterial({ color: 0xffffe5 });

/**
 * Model
 */
gltfLoader.load("portal.glb", (gltf) => {
	// gltf.scene.traverse((child) => {
	// 	// console.log(child)
	// 	child.material = bakedMaterial;
	// });

	// const poleLightAMesh = gltf.scene.children.find( (child) => {
	//     return child.name === 'poleLightA'
	// })
	// simplifed version of the code above
	const bakedMesh = gltf.scene.children.find(
		(child) => child.name === "all_merged"
	);
	const portalLightMesh = gltf.scene.children.find(
		(child) => child.name === "portalLight"
	);
	const poleLightAMesh = gltf.scene.children.find(
		(child) => child.name === "poleLightA"
	);
	const poleLightBMesh = gltf.scene.children.find(
		(child) => child.name === "poleLightB"
	);

	bakedMesh.material = bakedMaterial;
	poleLightBMesh.material = poleLightMaterial;
	poleLightAMesh.material = poleLightMaterial;
	poleLightBMesh.material = poleLightMaterial;
	portalLightMesh.material = portalLightMaterial;

	scene.add(gltf.scene);
	// console.log(gltf.scene);
});

/**
 * Fireflies
 */
// geometry
const firefliesGeometry = new THREE.BufferGeometry();
const firefliesCount = 100;
const positionArry = new Float32Array(firefliesCount * 3);
const scaleArray = new Float32Array(firefliesCount);

for (let i = 0; i < firefliesCount; i++) {
	positionArry[i * 3 + 0] = (Math.random() - 0.5) * 4;
	positionArry[i * 3 + 1] = Math.random() * 2;
	positionArry[i * 3 + 2] = (Math.random() - 0.5) * 4;

	scaleArray[i] = Math.random();
}

firefliesGeometry.setAttribute(
	"position",
	new THREE.BufferAttribute(positionArry, 3)
);

firefliesGeometry.setAttribute(
	"aScale",
	new THREE.BufferAttribute(scaleArray, 1)
);

// material
// const firefliesMat = new THREE.PointsMaterial({
// 	size: 0.1,
// 	setAttenuation: true,
// });
const firefliesMat = new THREE.ShaderMaterial({
	uniforms: {
		uTime: { value: 0 },
		uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
		uSize: { value: 100 },
	},
	vertexShader: firefliesVertexShader,
	fragmentShader: firefliesFragmentShader,
	transparent: true,
	blending: THREE.AdditiveBlending, // bad for performance !
	depthWrite: false,
});

// prettier-ignore
gui.add(firefliesMat.uniforms.uSize, 'value')
	.min(0)
	.max(500)
	.step(1)
	.name('fireflies size')

// points
const fireflies = new THREE.Points(firefliesGeometry, firefliesMat);
scene.add(fireflies);

/**
 * Sizes
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener("resize", () => {
	// Update sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	// Update camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Update renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

	// update fireflies
	firefliesMat.uniforms.uPixelRatio.value = Math.min(
		window.devicePixelRatio,
		2
	);
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
	45,
	sizes.width / sizes.height,
	0.1,
	100
);
camera.position.x = 4;
camera.position.y = 2;
camera.position.z = 4;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
	antialias: true,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputEncoding = THREE.sRGBEncoding;

// clear background
debugObject.clearColor = "#201919";
renderer.setClearColor(debugObject.clearColor);
// prettier-ignore
gui
	.addColor(debugObject, "clearColor")
	.onChange(() => {
		renderer.setClearColor(debugObject.clearColor);
	});

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
	const elapsedTime = clock.getElapsedTime();

	// update material
	firefliesMat.uniforms.uTime.value = elapsedTime;
	portalLightMaterial.uniforms.uTime.value = elapsedTime;

	// Update controls
	controls.update();

	// Render
	renderer.render(scene, camera);

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
